import React, { useCallback, useEffect, useMemo } from 'react';

import type { FlowId, Step, StepId } from '../types';

import { useWizard } from './WizardContext';

import type { WizardProgress } from './utils/progress';

import { resolveFlow } from './flowRegistry';

import { useWizardStore } from './wizardStore';

import { ActivityBoundary } from '../components/ActivityBoundary';

import { CompletedFlowGuard } from '../components/shared/CompletedFlowGuard';

import { useCompletionDetection } from './hooks/useCompletionDetection';

import { useFlowRestart } from './hooks/useFlowRestart';

import { WizardProgressBar } from './components/WizardProgressBar';

import type { JourneyItem } from './components/WizardStepHistory';

const completedFlowCache = new Set<FlowId>();

interface WizardShellProps {
  isLoading: boolean;
  currentStepId: StepId;
  progress: WizardProgress;
  stepName?: string;
}

export const WizardShell: React.FC<WizardShellProps> = ({
  isLoading,
  currentStepId,
  progress,
  stepName,
}) => {
  const { selfServiceData, flowId, goToStep, sessionId } = useWizard();

  const storeHasCompletedFlow = useWizardStore(
    (state) => state.hasCompletedFlow,
  );

  const setHasCompletedFlow = useWizardStore(
    (state) => state.setHasCompletedFlow,
  );

  const activeFlow = useMemo(() => resolveFlow(flowId), [flowId]);
  const steps = activeFlow.steps;
  const journey = progress.journey;

  const currentJourneyIndex = useMemo(
    () => journey.indexOf(currentStepId),
    [journey, currentStepId],
  );

  // Use completion detection hook
  const {
    isCompletionStep,
    hasCompletedFlow,
    completionStepIds,
    completionFlagKey,
  } = useCompletionDetection({
    flowId,
    sessionId,
    steps,
    currentStepId,
    selfServiceData,
    storeHasCompletedFlow,
    completedFlowCache,
  });

  // Use flow restart hook
  const { handleRestart } = useFlowRestart({
    flowId,
    sessionId,
    completedFlowCache,
    setHasCompletedFlow,
  });

  // Persist completion flags to sessionStorage
  useEffect(() => {
    if (!completionFlagKey || typeof window === 'undefined') {
      return;
    }

    if (
      selfServiceData.isCompleted === true ||
      isCompletionStep ||
      storeHasCompletedFlow
    ) {
      completedFlowCache.add(flowId);
      if (!storeHasCompletedFlow) {
        setHasCompletedFlow(true);
      }

      try {
        const sessionCompletionKey = sessionId
          ? `wizardCompleted:${sessionId}`
          : null;
        const flowCompletionKey = `wizardCompleted:flow:${flowId}`;

        if (sessionCompletionKey) {
          window.sessionStorage.setItem(sessionCompletionKey, 'true');
        }
        window.sessionStorage.setItem(flowCompletionKey, 'true');
      } catch {
        // Ignore storage errors
      }
    }
  }, [
    completionFlagKey,
    flowId,
    isCompletionStep,
    selfServiceData.isCompleted,
    sessionId,
    storeHasCompletedFlow,
    setHasCompletedFlow,
  ]);

  const getStepMeta = useCallback(
    (stepId: StepId): Step | undefined => {
      return steps[stepId];
    },
    [steps],
  );

  const handleInternalJump = useCallback(
    (targetStepId: StepId) => {
      if (isCompletionStep || targetStepId === currentStepId) {
        return;
      }

      const targetIndex = journey.indexOf(targetStepId);

      if (
        targetIndex === -1 ||
        currentJourneyIndex === -1 ||
        targetIndex >= currentJourneyIndex
      ) {
        return;
      }

      const trimmedHistory = journey.slice(0, targetIndex);
      useWizardStore.setState({ history: trimmedHistory });
      goToStep(targetStepId, false);
    },
    [currentJourneyIndex, currentStepId, goToStep, isCompletionStep, journey],
  );

  const journeyItems: JourneyItem[] = useMemo(() => {
    return journey.map((stepId, index) => {
      const meta = getStepMeta(stepId);
      const label = meta?.name ?? stepId;
      const isCurrent = index === currentJourneyIndex;
      const canJump =
        !isCompletionStep &&
        currentJourneyIndex > -1 &&
        index < currentJourneyIndex;

      return {
        stepId,
        label,
        index,
        isCurrent,
        canJump,
      };
    });
  }, [journey, getStepMeta, currentJourneyIndex, isCompletionStep]);

  const flowComponents = useMemo(() => {
    return Object.keys(steps).map((stepId) => ({
      stepId,
      Component: steps[stepId as StepId]?.component ?? null,
    }));
  }, [steps]);

  if (isLoading) {
    return (
      <div className={`wizard-panel ${currentStepId}`}>
        <div className="step-content">
          <div className="loading-text" aria-live="polite">
            <div className="spinner" role="status" aria-hidden="true"></div>
            <span className="visually-hidden">Indlæser...</span>
          </div>
        </div>
      </div>
    );
  }

  const stepTitle =
    stepName ?? getStepMeta(currentStepId)?.name ?? currentStepId;

  const stepNumber = Math.max(1, progress.stepIndex);

  if (hasCompletedFlow && !isCompletionStep) {
    return (
      <div className="wizard-panel">
        <CompletedFlowGuard onRestart={handleRestart} />
      </div>
    );
  }

  return (
    <div className="wizard-panel">
      <WizardProgressBar
        progress={progress}
        stepName={stepTitle}
        userName={selfServiceData.user?.name}
        stepNumber={stepNumber}
        journeyItems={journeyItems}
        currentStepId={currentStepId}
        onStepClick={handleInternalJump}
      />

      <div className="wizard-step-viewport">
        {flowComponents.map(({ stepId, Component }) => {
          const isActive = stepId === currentStepId;

          return (
            <ActivityBoundary
              key={stepId}
              mode={isActive ? 'visible' : 'hidden'}
            >
              <div
                className="wizard-step"
                hidden={!isActive}
                aria-hidden={!isActive}
              >
                {Component ? (
                  <Component />
                ) : isActive ? (
                  <div className="step-content">
                    <h1>Trin ikke fundet</h1>
                  </div>
                ) : null}
              </div>
            </ActivityBoundary>
          );
        })}
      </div>
    </div>
  );
};
